import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FaExternalLinkAlt } from "react-icons/fa";
import { isAbsoluteHttpUrl } from "../../utils/downloadConstants";
import "./SideDrawer.css"; // Import external CSS

const AZCOPY_DOCS_URL =
  "https://learn.microsoft.com/en-us/azure/storage/common/storage-use-azcopy-v10";

const INSTRUCTIONS_OPEN_ERROR =
  "Could not open download instructions. Please try again or contact support.";

const SideDrawer = ({
  isOpen,
  onClose,
  fileName,
  sizeInGB,
  fileUrl,
  instructionUrl,
  resolveInstructionUrl,
  handleBrowserDownload,
}) => {
  const [copyState, setCopyState] = useState("Copy");
  const drawerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        onClose(); // Close the drawer when clicked outside
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const instructionHref = isAbsoluteHttpUrl(instructionUrl)
    ? instructionUrl.trim()
    : undefined;

  const openInstructions = async (event) => {
    // A missing href is the current DMQT page, which opens the Navix map.
    if (instructionHref) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof resolveInstructionUrl !== "function") {
      alert(INSTRUCTIONS_OPEN_ERROR);
      return;
    }

    const pending = window.open("", "_blank");
    try {
      const url = await resolveInstructionUrl();
      if (!isAbsoluteHttpUrl(url)) {
        pending?.close();
        alert(INSTRUCTIONS_OPEN_ERROR);
        return;
      }

      const trimmed = url.trim();
      if (pending) {
        pending.opener = null;
        pending.location.replace(trimmed);
        return;
      }

      // Popup may have been blocked; try again with the real URL.
      const opened = window.open(trimmed, "_blank", "noopener,noreferrer");
      if (!opened) {
        alert(INSTRUCTIONS_OPEN_ERROR);
      }
    } catch (err) {
      pending?.close();
      console.warn("Could not mint SAS for download instructions PDF:", err);
      alert(INSTRUCTIONS_OPEN_ERROR);
    }
  };

  const handleClipboardCopy = (url) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopyState("Copied");
        setTimeout(() => setCopyState("Copy"), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy URL: ", err);
      });
  };

  return (
    <div className={`side-drawer ${isOpen ? "open" : ""}`} ref={drawerRef}>
      <div className="drawer-header">
        <h3>Download Options</h3>
        <button className="close-button" onClick={onClose}>
          ✕
        </button>
      </div>
      <p className="file-info">
        The file <strong>{fileName}</strong> exceeds 10 GB (
        {sizeInGB.toFixed(2)} GB). Browser downloads may be slow. For faster
        performance, consider using{" "}
        <a href={AZCOPY_DOCS_URL} target="_blank" rel="noopener noreferrer">
          <strong>AzCopy</strong>{" "}
          <FaExternalLinkAlt className="external-link-icon" />
        </a>
        {"."}
      </p>
      <button className="download-btn" onClick={handleBrowserDownload}>
        Download via Browser
      </button>

      <div className="copy-url">
        <div className="instruction-link">
          <FaExternalLinkAlt className="instruction-icon" />
          <a
            href={instructionHref}
            target="_blank"
            rel="noopener noreferrer"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={openInstructions}
          >
            Instructions for AzCopy Downloads
          </a>
        </div>

        <div className="storage-explorer-section">
          <p className="url-label">AzCopy SAS URL:</p>
          <div className="url-container" title={fileUrl}>
            <div className="url-box">
              <span className="url-text">{fileUrl}</span>
              <button
                className={`copy-btn ${copyState === "Copied" ? "copied" : ""}`}
                onClick={() => handleClipboardCopy(fileUrl)}
              >
                {copyState}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SideDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  fileName: PropTypes.string,
  sizeInGB: PropTypes.number,
  fileUrl: PropTypes.string,
  instructionUrl: PropTypes.string,
  resolveInstructionUrl: PropTypes.func,
  handleBrowserDownload: PropTypes.func,
};

export default SideDrawer;
